"use client";

import * as React from "react";
import { useToast } from "@/app/_module/hooks/use-toast";
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/app/_module/components/ui/toast";

// Memoized toast item to prevent re-renders triggering compose-refs infinite loop
const MemoizedToast = React.memo(function MemoizedToast({
  id,
  title,
  description,
  action,
  open,
  variant,
  onOpenChange,
  className,
}: {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactElement;
  open?: boolean;
  variant?: "default" | "destructive";
  onOpenChange?: (open: boolean) => void;
  /**
   * Per-toast styling from the toast({ className }) call site.
   *
   * This was accepted by toast() and stored on the toast, but never made it
   * here — so every colour a call site asked for was silently dropped and all
   * 13 of them rendered as the default white. Toast merges it with twMerge, so
   * a bg-* from the call site correctly overrides the variant's own bg.
   */
  className?: string;
}) {
  return (
    <Toast open={open} duration={3000} variant={variant} onOpenChange={onOpenChange} className={className}>
      <div className="grid gap-1">
        {title && <ToastTitle>{title}</ToastTitle>}
        {description && <ToastDescription>{description}</ToastDescription>}
      </div>
      {action}
      <ToastClose />
    </Toast>
  );
});

export function Toaster() {
  const { toasts } = useToast();

  // Memoize the viewport to prevent ref composition issues
  const viewport = React.useMemo(() => <ToastViewport />, []);

  return (
    <ToastProvider swipeDirection="right">
      {viewport}

      {toasts.map(({ id, title, description, action, open, variant, onOpenChange, className }) => (
        <MemoizedToast
          key={id}
          id={id}
          title={title}
          description={description}
          action={action}
          open={open}
          variant={variant ?? undefined}
          onOpenChange={onOpenChange}
          className={className}
        />
      ))}
    </ToastProvider>
  );
}
